<template>
  <div class="default-layout" :class="{ 'is-sidebar-mini': !isMobile && isSidebarMini }">
    <!-- Верхняя панель -->
    <HeadBar
      :drawer-open="isSidebarNavVisible"
      :menu-toggle-always-bars="menuToggleAlwaysBars"
      @toggle-side-nav="toggleSidebar"
      class="layout-header"
    />

    <!-- Боковое меню -->
    <Sidebar :is-open="isSidebarOpen" :is-mini="isSidebarMini" :is-mobile="isMobile" @toggle-mini="toggleSidebarMini"
      @close="closeSidebar" />

    <!-- Основной контент -->
    <main
      class="layout-main"
      :class="{ 'layout-main--clip': isPlatformViewport }"
      :style="contentStyles"
    >
      <div class="layout-content">
        <MLoader />

        <PlatformDisconnectBanner />

        <!-- Breadcrumb navigation -->
        <MBreadcrumb v-if="showBreadcrumb" />

        <RouterView v-slot="{ Component }">
          <Transition name="page-fade" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
      </div>
    </main>

    <Certificates />
    <FileSelectDialog />
    <DialogContainer />
    <SpdHoverTooltip v-if="isSpdClient" />

  </div>
</template>

<script setup lang="ts">
import { useMenu } from '@/common/composables/useMenu'
import { useWebSocketAutoConnect } from '@/common/composables/useWebSocket'
import { useMenuCounters } from '@/common/composables/useMenuCounters'
import { useSystemEvents } from '@/common/composables/useSystemEvents'
import HeadBar from '@/components/app/HeadBar.vue'
import Sidebar from '@/components/app/Sidebar.vue'
import PlatformDisconnectBanner from '@/components/app/PlatformDisconnectBanner.vue'
import MBreadcrumb from '@/components/ui/MBreadcrumb.vue'
import MLoader from '@/components/ui/MLoader.vue'
import DialogContainer from '@/components/platform/dialog/DialogContainer.vue'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Certificates from '@/components/app/Certificates.vue'
import FileSelectDialog from '@/components/app/FileSelectDialog.vue'
import SpdHoverTooltip from '@/components/app/SpdHoverTooltip.vue'
import { startupActionService } from '@/services/startup/startupAction.service'
import { MAIN_FRAME_SLOT_ID, isMainFramePageData } from '@/common/utils/mainFrameSlot'
import { formHasRenderableLayout } from '@/services/parse/parse.service'
import { useRoute } from 'vue-router'
import { configService } from '@/common/utils/clientConfig'

const appStore = useAppStore()
const userStore = useUserStore()
const route = useRoute()
const { loadMenu, hasMenu, sideMenu } = useMenu()

useWebSocketAutoConnect()

useMenuCounters()
useSystemEvents()

// Флаг для отслеживания загрузки меню
const menuLoaded = ref(false)

/** Drawer mode: content never offsets beside sidebar (matches plan <768, keep 960 for tablets). */
const MOBILE_DRAWER_MQ = '(max-width: 960px)'

function readIsMobileDrawer(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia(MOBILE_DRAWER_MQ).matches
}

// Состояние sidebar — init from viewport to avoid first-paint rail eating width
const isMobile = ref(readIsMobileDrawer())
const isSidebarOpen = ref(!isMobile.value)
const isSidebarMini = ref(false)

const showBreadcrumb = computed(() => {
  if (route.name !== 'home') return true
  const pageData = appStore.pagesData[MAIN_FRAME_SLOT_ID]
  if (!isMainFramePageData(pageData)) return true
  return !formHasRenderableLayout(pageData?.form)
})

const isPlatformViewport = computed(() => {
  const name = String(route.name ?? '')
  return name === 'platform' || name === 'document'
})

const isSidebarNavVisible = computed(() =>
  isMobile.value ? isSidebarOpen.value : !isSidebarMini.value,
)

const menuToggleAlwaysBars = computed(
  () => !isMobile.value && configService.isSpd(),
)

const isSpdClient = computed(() => configService.isSpd())

// Стили для основного контента
const contentStyles = computed(() => {
  const sidebarReservesSpace = !isMobile.value && isSidebarOpen.value
  const sidebarOffset = sidebarReservesSpace ? 'var(--sidebar-width, 22rem)' : '0px'
  if (!sidebarReservesSpace) {
    return {
      left: '0',
      width: '100%',
      '--layout-sidebar-offset': sidebarOffset,
    }
  }

  return {
    left: isSidebarMini.value ? 'var(--sidebar-mini-width, 5rem)' : 'var(--sidebar-width, 22rem)',
    width: isSidebarMini.value
      ? 'calc(100% - var(--sidebar-mini-width, 5rem))'
      : 'calc(100% - var(--sidebar-width, 22rem))',
    '--layout-sidebar-offset': sidebarOffset,
  }
})

// Обработчики responsive
const checkMobile = () => {
  const nextMobile = readIsMobileDrawer()
  const wasMobile = isMobile.value
  isMobile.value = nextMobile
  if (nextMobile) {
    isSidebarMini.value = false
    // Close when entering drawer mode; keep state while resizing within mobile
    if (!wasMobile) {
      isSidebarOpen.value = false
    }
  } else if (wasMobile) {
    isSidebarOpen.value = true
  }
}

const handleResize = () => {
  checkMobile()
}

// Методы управления sidebar
const toggleSidebar = () => {
  if (isMobile.value) {
    isSidebarOpen.value = !isSidebarOpen.value
  } else {
    isSidebarMini.value = !isSidebarMini.value
  }
}

const toggleSidebarMini = () => {
  isSidebarMini.value = !isSidebarMini.value
}

const closeSidebar = () => {
  if (isMobile.value) {
    isSidebarOpen.value = false
  }
}

onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', handleResize)

  // Загружаем меню только один раз при монтировании лейаута
  if (userStore.isAuthenticated && !hasMenu.value && !menuLoaded.value) {
    try {
      await loadMenu()
      menuLoaded.value = true
    } catch (error) {
      console.error('DefaultLayout: Failed to load menu:', error)
      menuLoaded.value = true // Помечаем как попытку загрузки
    }
  }

  if (userStore.isAuthenticated) {
    void startupActionService.runStartupActions(appStore)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped lang="scss">
.default-layout {
  position: relative;
  height: 100%;
  min-height: 100%;
}

.layout-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-height, 4rem);
}

.layout-main {
  position: fixed;
  top: var(--header-height, 4rem);
  left: 0;
  right: 0;
  bottom: 0;
  background: #f8f9fa;
  transition: all 0.3s ease;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.layout-main--clip {
  overflow: hidden;
  scrollbar-gutter: auto;
}

.layout-content {
  padding: 24px 24px calc(24px + var(--app-safe-bottom, 0px));
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.layout-main--clip .layout-content {
  overflow: hidden;
  min-height: 0;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 960px) {
  .layout-header {
    height: 3.5rem;
  }

  .layout-main {
    top: 3.5rem;
  }

  .layout-content {
    padding: 16px 16px calc(16px + var(--app-safe-bottom, 0px));
  }
}

@media (max-width: 600px) {
  .layout-content {
    padding: 10px 10px calc(10px + var(--app-safe-bottom, 0px));
  }
}
</style>
